import { useState, useRef, useCallback, useEffect } from 'react';
import { Icon, type IconKey } from '../../icons';
import './FloatingPanel.css';

interface FloatingPanelProps {
  initialPosition: { x: number; y: number };
  initialSize: { width: number; height: number };
  minSize?: { width: number; height: number };
  zIndex?: number;
  title?: string;
  onClose?: () => void;
  closeIcon?: IconKey;
  closeTitle?: string;
  /** Supplying onMinimize adds a minimize button; the panel is then hidden, not unmounted. */
  onMinimize?: () => void;
  minimized?: boolean;
  onRestore?: () => void;
  minimizedIcon?: IconKey;
  onPositionChange?: (pos: { x: number; y: number }) => void;
  onSizeChange?: (size: { width: number; height: number }) => void;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}

export default function FloatingPanel({
  initialPosition,
  initialSize,
  minSize = { width: 320, height: 240 },
  zIndex = 8000,
  title,
  onClose,
  closeIcon = 'chev-down',
  closeTitle = 'Dock to sidebar',
  onMinimize,
  minimized = false,
  onRestore,
  minimizedIcon = 'expand',
  onPositionChange,
  onSizeChange,
  headerExtra,
  children,
}: FloatingPanelProps) {
  const [pos, setPos] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const posRef = useRef(pos);
  const sizeRef = useRef(size);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  const onSizeChangeRef = useRef(onSizeChange);

  onPositionChangeRef.current = onPositionChange;
  onSizeChangeRef.current = onSizeChange;

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: posRef.current.x, startPosY: posRef.current.y };
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startW: sizeRef.current.width, startH: sizeRef.current.height };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        const newPos = {
          x: Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.startPosX + dx)),
          y: Math.max(0, Math.min(window.innerHeight - 50, dragRef.current.startPosY + dy)),
        };
        posRef.current = newPos;
        setPos(newPos);
      }
      if (resizeRef.current) {
        const dx = e.clientX - resizeRef.current.startX;
        const dy = e.clientY - resizeRef.current.startY;
        const newSize = {
          width: Math.max(minSize.width, resizeRef.current.startW + dx),
          height: Math.max(minSize.height, resizeRef.current.startH + dy),
        };
        sizeRef.current = newSize;
        setSize(newSize);
      }
    };

    const handleMouseUp = () => {
      if (dragRef.current) {
        dragRef.current = null;
        onPositionChangeRef.current?.(posRef.current);
      }
      if (resizeRef.current) {
        resizeRef.current = null;
        onSizeChangeRef.current?.(sizeRef.current);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [minSize.width, minSize.height]);

  // Minimizing hides the panel rather than unmounting it, so the caller's state — chat
  // history, scroll position, a half-typed message — survives the round trip, and so do
  // this panel's own position and size.
  return (
    <>
      <div
        className="floating-panel"
        style={{
          left: pos.x,
          top: pos.y,
          width: size.width,
          height: size.height,
          zIndex,
          display: minimized ? 'none' : undefined,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="floating-panel-header" onMouseDown={handleDragStart}>
          {title && <span className="floating-panel-title">{title}</span>}
          <div className="floating-panel-header-actions">
            {headerExtra}
            {onMinimize && (
              <button className="floating-panel-btn" onClick={onMinimize} title="Minimize" aria-label="Minimize">
                <Icon name="collapse" />
              </button>
            )}
            {onClose && (
              <button className="floating-panel-btn" onClick={onClose} title={closeTitle} aria-label={closeTitle}>
                <Icon name={closeIcon} />
              </button>
            )}
          </div>
        </div>
        <div className="floating-panel-body">
          {children}
        </div>
        <div className="floating-panel-resize-handle" onMouseDown={handleResizeStart} />
      </div>

      {minimized && onRestore && (
        <button
          type="button"
          className="floating-panel-dock"
          style={{ zIndex }}
          onClick={onRestore}
          title={title ? `Restore ${title}` : 'Restore'}
        >
          <Icon name={minimizedIcon} />
          {title && <span className="floating-panel-dock-label">{title}</span>}
        </button>
      )}
    </>
  );
}
